import { Link } from '@tanstack/react-router'
import { SectionHeading } from '~/components/ui/SectionHeading'
import { YouTubeEmbed } from '~/components/ui/YouTubeEmbed'
import { pageMeta, absoluteUrl } from '~/lib/site/seo'
import { Container } from '~/components/ui/Container'
import { Img } from '~/components/ui/Img'
import pigs from '~/assets/images/whistleblower-pigs.jpg?responsive'
import smithfield from '~/assets/images/whistleblower-smithfield.jpg?responsive'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

// Webflow had a blurb under each card, hidden there (display: none), so it's
// left out.
const reports = [
  {
    href: 'https://theintercept.com/2020/05/29/pigs-factory-farms-ventilation-shutdown-coronavirus/',
    img: pigs,
    alt: 'Drone footage of dead pigs from mass extermination in Iowa amid corona virus pandemic',
    title:
      'Hidden Video and Whistleblower Reveal Gruesome Mass-Extermination Method for Iowa Pigs Amid Pandemic',
  },
  {
    href: 'https://theintercept.com/2020/04/19/smithfield-foods-wisconsin-coronavirus/',
    img: smithfield,
    alt: '',
    title:
      'Employees Say Smithfield Plant In Wisconsin Concealed COVID-19 Infections, Pressured Them To Work Elbow To Elbow Without Protection',
  },
]

const uploadUrl = 'https://upload.dxe.io/?mode=whistle'

const linkClass = 'font-bold text-accent hover:underline'

// The press articles are only available in English, so the Spanish page
// shows them untranslated.
const copy = {
  en: {
    path: '/whistleblower-support',
    otherPath: '/es/whistleblower-support',
    title: 'Whistleblower Helpline',
    description:
      'You have the right to report violations in your workplace. Submit an anonymous report and the DxE whistleblower support team will help you expose the abuse.',
    ogDescription:
      'Submit an anonymous report to the DxE whistleblower support team.',
    intro: (
      <>
        You have the right to report violations in your workplace.
        <br />
        <br />
        Have you experienced abuse or unsafe conditions at work? Have you
        witnessed animal cruelty?
        <br />
        You can submit an anonymous report here, and the DxE whistleblower
        support team will help you expose the abuse. Our lawyers, investigators,
        and media experts are ready to help.
      </>
    ),
    switchLabel: 'Haga clic aquí para leer esta página en español',
    formTitle: 'Whistleblower report form',
    newTab: 'Open the form in a new tab',
    note: 'NOTE: Our legal team considers this a prospective attorney client relationship, but we cannot guarantee any amount of concrete legal support.',
    reportsHeading: 'Press Articles from Past Reports',
  },
  es: {
    path: '/es/whistleblower-support',
    otherPath: '/whistleblower-support',
    title: 'Apoyo a denunciantes',
    description:
      'La agricultura animal no solo es devastadora para los animales no humanos, sino también para los seres humanos que viven o trabajan cerca de sus instalaciones. El equipo legal de DxE quiere apoyar.',
    ogDescription:
      'Envíe un informe anónimo al equipo de apoyo a denunciantes de DxE.',
    intro: (
      <>
        La agricultura animal no solo es devastadora para los animales no
        humanos, sino también para los seres humanos que viven o trabajan cerca
        de sus instalaciones.
        <br />
        <br />
        El equipo legal de DxE quiere apoyar si usted tiene alguna preocupación
        acerca de la industria. Comuníquese con nosotros a{' '}
        <a href="mailto:legal@dxe.io" className="text-accent hover:underline">
          legal@dxe.io
        </a>{' '}
        y/o envíe cualquier archivo aquí.
      </>
    ),
    switchLabel: 'English',
    formTitle: 'Formulario de informe de denunciantes',
    newTab: 'Abrir el formulario en una pestaña nueva',
    note: 'NOTA: Nuestro equipo legal considera que esto es una posible relación de abogado y cliente, pero no podemos garantizar ningún tipo de apoyo legal concreto.',
    reportsHeading: 'Press Articles from Past Reports',
  },
}

export type WhistleblowerLang = keyof typeof copy

/** `head()` for the English and Spanish routes: meta plus hreflang alternates. */
export function whistleblowerHead(lang: WhistleblowerLang) {
  const c = copy[lang]
  return {
    ...pageMeta({
      title: c.title,
      description: c.description,
      ogDescription: c.ogDescription,
    }),
    links: [
      { rel: 'alternate', hrefLang: 'en', href: absoluteUrl(copy.en.path) },
      { rel: 'alternate', hrefLang: 'es', href: absoluteUrl(copy.es.path) },
    ],
  }
}

/** /whistleblower-support (en) and /es/whistleblower-support (es). */
export function WhistleblowerPage({ lang }: { lang: WhistleblowerLang }) {
  const c = copy[lang]
  return (
    <PageHero bottom="section" lang={lang}>
      <Container width="narrow">
        <PageTitle className="mb-5 text-center">{c.title}</PageTitle>
        <YouTubeEmbed
          id="lZdZI8I5otI"
          title={c.title}
          className="mt-6 mb-[25px] md:mt-10"
        />
        <p className="mx-auto max-w-[700px] text-center text-lg leading-[26px]">
          {c.intro}
          <br />
          <br />
          <Link
            to={c.otherPath}
            lang={lang === 'en' ? 'es' : 'en'}
            hrefLang={lang === 'en' ? 'es' : 'en'}
            className={linkClass}
          >
            {c.switchLabel}
          </Link>
        </p>
      </Container>

      <Container width="narrow" className="pt-20">
        <iframe
          src={uploadUrl}
          title={c.formTitle}
          loading="lazy"
          className="h-[760px] w-full rounded-lg border border-line bg-white md:h-[680px]"
        />
        <p className="mt-4 text-center text-base">
          <a
            href={uploadUrl}
            target="_blank"
            rel="noreferrer"
            className={linkClass}
          >
            {c.newTab}
          </a>
        </p>
        <p className="mt-5 text-sm text-fg-muted">{c.note}</p>
      </Container>

      <Container width="narrow" className="pt-20" lang="en">
        <SectionHeading className="mb-6">{c.reportsHeading}</SectionHeading>
        <div className="grid gap-10 md:grid-cols-2">
          {reports.map((r) => (
            <div key={r.href}>
              <a href={r.href} target="_blank" rel="noreferrer">
                <Img
                  image={r.img}
                  sizes="(min-width: 992px) 450px, (min-width: 768px) 45vw, 90vw"
                  alt={r.alt}
                  className="my-2.5 w-full"
                />
                <h3 className="text-xl leading-7 font-bold text-fg">
                  {r.title}
                </h3>
              </a>
            </div>
          ))}
        </div>
      </Container>
    </PageHero>
  )
}
